import { api } from '../api.js';
import { canDo, getCurrentUser } from '../auth.js';
import { getBasePath } from '../router.js';
import { MODULES, findItem, visibleItems } from '../utils/erp-modules.js';
import { BRAND } from '../utils/brand.js';
import { iconMenu } from '../utils/icons.js';

let _badgePollIntervalId = null;
let _navModuleKey = null;
let _navBound = false;

const ROLE_LABEL = {
  owner: 'Собственник',
  admin: 'Админ',
  manager: 'Менеджер',
  user: 'Пользователь',
};

// Колокольчик остаётся в шапке в любом модуле (id используются в refreshBadge).
const BELL_HTML = `<a href="/my-day" data-route="/my-day" id="notifications-bell" title="Уведомления">🔔
        <span id="notifications-badge" class="header-badge" hidden>0</span>
      </a>`;

// Вкладки модуля CCPM — нынешний набор без изменений (UI-1, план 2026-09-25).
function ccpmNavHtml() {
  return `
      <a href="/"                   data-route="/">Dashboard</a>
      <a href="/my-day"             data-route="/my-day">Мой день</a>
      <a href="/gantt"              data-route="/gantt">Gantt</a>
      <a href="/resources"          data-route="/resources">Ресурсы</a>
      <a href="/volume"             data-route="/volume">Объём</a>
      <a href="/fever"              data-route="/fever">Fever</a>
      <a href="/pipeline"           data-route="/pipeline">Pipeline</a>
      <a href="/contractor-updates" data-route="/contractor-updates">Подрядчики
        <span id="contractor-badge" class="header-badge" hidden>0</span>
      </a>
      ${BELL_HTML}
      <a href="/audit"              data-route="/audit">Аудит</a>
      <a href="/calibration"        data-route="/calibration">Калибрация</a>
      ${canDo('user.read') ? `<a href="/admin" data-route="/admin">Админ</a>` : ''}
  `;
}

// Вкладки остальных модулей = подпункты модуля из utils/erp-modules.js.
function moduleNavHtml(mod) {
  const tabs = visibleItems(mod, canDo)
    .map(it => `<a href="${it.path}" data-route="${it.path}">${it.label}</a>`)
    .join('\n      ');
  return `\n      ${tabs}\n      ${BELL_HTML}\n  `;
}

function moduleForPath(path) {
  return findItem(getBasePath(path))?.module ?? MODULES.find(m => m.key === 'ccpm');
}

/** Перерисовать вкладки под активный модуль (вызывается на каждую навигацию). */
function renderNav(path) {
  const nav = document.querySelector('#app-header .header-nav');
  if (!nav) return;
  const mod = moduleForPath(path);
  if (_navModuleKey === mod.key) return;
  _navModuleKey = mod.key;
  nav.innerHTML = mod.key === 'ccpm' ? ccpmNavHtml() : moduleNavHtml(mod);
  refreshBadge();   // бейджи в свежей разметке, не ждать следующего опроса
}

export function initHeader() {
  const header = document.getElementById('app-header');
  if (!header) return;

  const u = getCurrentUser();
  const userLabel = u ? `${u.full_name} · ${ROLE_LABEL[u.role] || u.role}` : '—';

  header.innerHTML = `
    <button type="button" class="header-burger" data-sidebar-toggle
            aria-label="Меню" aria-expanded="false" title="Меню">${iconMenu(18)}</button>
    <div class="header-logo" data-brand>
      <span class="brand-mark" aria-hidden="true"></span>
      <span class="brand-name">${BRAND}</span>
    </div>
    <nav class="header-nav"></nav>
    <div class="header-right">
      <select id="tenant-selector">
        <option value="personal">Personal</option>
      </select>
      <span id="user-badge" title="${u ? '@' + u.login : ''}">${userLabel}</span>
      <button class="toolbar-btn" data-action="logout" title="Выйти">Выйти</button>
    </div>
  `;

  _navModuleKey = null;
  renderNav(location.pathname);
  if (!_navBound) {
    _navBound = true;
    document.addEventListener('ccpm:navigate', e => renderNav(e.detail?.path ?? location.pathname));
  }

  startBadgePolling();
}

function startBadgePolling() {
  refreshBadge();
  if (_badgePollIntervalId) clearInterval(_badgePollIntervalId);
  _badgePollIntervalId = setInterval(refreshBadge, 30_000);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (_badgePollIntervalId) {
        clearInterval(_badgePollIntervalId);
        _badgePollIntervalId = null;
      }
    } else if (!_badgePollIntervalId) {
      refreshBadge();
      _badgePollIntervalId = setInterval(refreshBadge, 30_000);
    }
  });
}

async function refreshBadge() {
  // Contractor updates badge (Phase 2).
  const el = document.getElementById('contractor-badge');
  if (el) {
    try {
      const { pending } = await api.contractorUpdatesBadge();
      if (pending > 0) {
        el.textContent = String(pending);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    } catch {
      /* silent — badge is non-critical */
    }
  }

  // Notifications bell badge (миграция 050, C1).
  const nb = document.getElementById('notifications-badge');
  if (nb) {
    try {
      const { unread } = await api.notifications.unreadCount();
      if (unread > 0) {
        nb.textContent = String(unread);
        nb.hidden = false;
      } else {
        nb.hidden = true;
      }
    } catch {
      /* silent */
    }
  }
}

// Exported so any view that mutates state (approve/reject) can poke a refresh.
export function refreshContractorBadge() {
  refreshBadge();
}
